import React, { useMemo } from "react";
import {
    Checkbox,
    SelectField,
    Spinner,
    StatePlaceholder,
    type CollectionKey,
    type CollectionState,
    type PickerItemRenderContext,
    type PickerOverlaySurface,
} from "@omega/ui";
import { createFieldPickerFullscreenRenderer, type FieldPickerFullscreenClose } from "@app/omega-ui/fieldPickerFullscreen";
import type { PickerOverlayMode } from "../overlay/picker-overlay";
import { resolveOptionSurfaceRender, type OptionSurfaceRenderContext, type OptionSurfaceSlots } from "../picker-option";
import "./Select.scss";

/** Данные опции для {@link Select}. */
export type SelectOptionData<V, TMeta = unknown> = {
    value: V;
    /** Default content опции (popover/fullscreen) и trigger, если нет overrides. */
    label: React.ReactNode;
    disabled?: boolean;
    meta?: TMeta;
};

export type SelectValue<M extends boolean, V> = M extends true ? V[] : V;

type SelectChangeValue<M extends boolean, V> = M extends true ? V[] : V | null;

/** Контекст render опции {@link Select}. */
export type SelectOptionRenderContext = OptionSurfaceRenderContext;

/** Per-surface overrides опций {@link Select}. */
export type SelectOptionSlots<T> = OptionSurfaceSlots<T>;

/** Пропсы {@link Select}. */
export interface SelectProps<T, V, M extends boolean = false> {
    label?: string;
    options: T[] | null;
    getOptionData: (option: T) => SelectOptionData<V, unknown>;
    value: SelectValue<M, V> | null | undefined;
    multiple?: M;
    className?: string;
    disabled?: boolean;
    required?: boolean;
    clearable?: boolean;
    onChange: (value: SelectChangeValue<M, V>) => void;
    error?: boolean;
    /**
     * Первичная / blocking-загрузка списка. \
     * Показывает `slots.states.loading` вместо опций. Не путать с {@link isRefreshing}. \
     * Если не задан, `options === null` тоже трактуется как loading (обратная совместимость).
     */
    isLoading?: boolean;
    /**
     * Фоновое обновление уже показанного списка. \
     * Список остаётся видимым; package рисует `LinearProgress` сверху.
     * @see {@link SelectField} `isRefreshing`
     */
    isRefreshing?: boolean;
    /** Вместо выбранного значения (загрузка, orphan id). */
    statusSlot?: React.ReactNode | null;
    placeholder?: string;
    /**
     * Точечные overrides рендера опции по surface. \
     * Задайте только нужный surface — остальные defaults Select сохраняются. \
     * Multiple: checkbox-chrome только в popover (снаружи слота); fullscreen — без чекбоксов.
     * @see {@link SelectOptionSlots}
     */
    optionSlots?: SelectOptionSlots<T>;
    /**
     * Режим поверхности выбора.
     * @default "auto"
     */
    overlayMode?: PickerOverlayMode;
    /**
     * Media query, при котором `overlayMode="auto"` переключается в fullscreen.
     * @default "(max-width: 640px), (pointer: coarse)"
     */
    fullscreenMediaQuery?: string;
    /** Заголовок fullscreen picker. Если не задан, используется `label` или `placeholder`. */
    fullscreenTitle?: React.ReactNode;
    /**
     * Нижнее действие закрытия fullscreen overlay. \
     * По умолчанию — `FieldPickerFullscreenCloseButton` («Вернуться назад» + стрелка влево). \
     * `false` — скрыть. Render prop — полная замена (можно переиспользовать дефолтную кнопку).
     * @param api.dismiss закрывает fullscreen overlay
     * @see {@link FieldPickerFullscreenClose}
     */
    renderFullscreenClose?: FieldPickerFullscreenClose;
}

type OptionId = string | number | boolean;

function isOptionId(value: unknown): value is OptionId {
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function toCollectionKey(value: OptionId): CollectionKey {
    if (typeof value === "boolean") {
        return value ? "true" : "false";
    }
    return value;
}

/**
 * Density для наполнения overlay: компактный popover vs читаемый fullscreen.
 * @param surface resolved surface от {@link SelectField}
 */
function overlayContentSize(surface: PickerOverlaySurface): "sm" | "md" {
    return surface === "fullscreen" ? "md" : "sm";
}

function optionLabelText(label: React.ReactNode): string {
    if (typeof label === "string" || typeof label === "number") {
        return String(label);
    }
    return "";
}

function defaultOptionChrome(label: React.ReactNode, surface: PickerOverlaySurface): React.ReactNode {
    return (
        <span
            className={
                surface === "fullscreen"
                    ? "omp-select__option omp-select__option--fullscreen"
                    : "omp-select__option omp-select__option--popover"
            }
        >
            {label}
        </span>
    );
}

/**
 * Controlled select: facade над `@omega/ui` SelectField.
 * Floating label; fullscreen shell — через `OmegaUiProvider` + footer close. \
 * Опции: `getOptionData().label` (content) + optional `optionSlots` (per-surface). \
 * Empty / loading placeholders следует `surface` (`sm` popover, `md` fullscreen). \
 * Blocking load → `isLoading` + `StatePlaceholder`; soft refetch → `isRefreshing` (package `LinearProgress`).
 */
export function Select<T, V, M extends boolean = false>({
    label,
    options,
    getOptionData,
    value,
    multiple,
    className,
    disabled = false,
    required = false,
    clearable = false,
    onChange,
    error = false,
    isLoading = false,
    isRefreshing = false,
    statusSlot,
    placeholder,
    optionSlots,
    overlayMode = "auto",
    fullscreenMediaQuery,
    fullscreenTitle,
    renderFullscreenClose,
}: SelectProps<T, V, M>) {
    const items = options ?? [];
    const isMultiple = multiple === true;

    const selectedIds = useMemo(() => {
        if (value == null) return isMultiple ? [] : null;
        if (isMultiple) {
            return Array.isArray(value) ? value : [];
        }
        return value;
    }, [value, isMultiple]);

    const getItemKey = (item: T): CollectionKey => {
        const raw = getOptionData(item).value;
        if (isOptionId(raw)) {
            return toCollectionKey(raw);
        }
        const index = items.indexOf(item);
        return index >= 0 ? index : 0;
    };

    const findValueByKey = (key: CollectionKey): V | null => {
        for (const option of items) {
            if (getItemKey(option) === key) {
                return getOptionData(option).value;
            }
        }
        return null;
    };

    const selectedKeys = useMemo(() => {
        if (statusSlot != null) return new Set<CollectionKey>();
        if (isMultiple) {
            const ids = Array.isArray(selectedIds) ? selectedIds : [];
            return new Set(
                items.flatMap((option) => {
                    const optionValue = getOptionData(option).value;
                    const matched = ids.some((id) => id === optionValue);
                    return matched ? [getItemKey(option)] : [];
                }),
            );
        }
        if (selectedIds == null || Array.isArray(selectedIds)) return null;
        const matched = items.find((option) => getOptionData(option).value === selectedIds);
        return matched != null ? getItemKey(matched) : null;
    }, [statusSlot, isMultiple, selectedIds, items, getOptionData]);

    const disabledKeys = useMemo(() => {
        return items.flatMap((option) => {
            const data = getOptionData(option);
            return data.disabled === true ? [getItemKey(option)] : [];
        });
    }, [items, getOptionData]);

    const collectionState: CollectionState | undefined = (() => {
        if (isLoading === true || options == null) return "loading";
        if (options.length === 0) return "empty";
        return undefined;
    })();

    const fieldLabel = label ?? placeholder;
    const collectionLabel =
        typeof fullscreenTitle === "string" || typeof fullscreenTitle === "number"
            ? String(fullscreenTitle)
            : (label ?? placeholder);

    const renderFullscreen = useMemo(
        () => createFieldPickerFullscreenRenderer(renderFullscreenClose),
        [renderFullscreenClose],
    );

    const optionDefaults = useMemo(
        () => ({
            popover: (option: T, _ctx: OptionSurfaceRenderContext) =>
                defaultOptionChrome(getOptionData(option).label, "popover"),
            fullscreen: (option: T, _ctx: OptionSurfaceRenderContext) =>
                defaultOptionChrome(getOptionData(option).label, "fullscreen"),
        }),
        [getOptionData],
    );

    const renderListItem = (item: T, context: PickerItemRenderContext) => {
        const optionCtx: OptionSurfaceRenderContext = {
            selected: context.selected,
            disabled: context.disabled,
            surface: context.surface,
        };
        const render = resolveOptionSurfaceRender(context.surface, optionSlots, optionDefaults);
        const content = render(item, optionCtx);
        // Checkbox chrome — только popover (компактная плотность). \
        // Fullscreen опирается на selected-стиль list-box item.
        if (!isMultiple || context.surface !== "popover") {
            return content;
        }
        return (
            <span className="omp-select__option-row">
                <Checkbox
                    checked={context.selected}
                    checkIconSize="10px"
                    checkboxSize="14px"
                    effectSize="14px"
                    presentation
                    size="sm"
                />
                <span className="omp-select__option-row-content">{content}</span>
            </span>
        );
    };

    const commonProps = {
        classes: {
            overlay: "spp-ui-theme omp-omega-ui",
        },
        clearable: clearable && statusSlot == null,
        collectionLabel,
        disabled,
        disabledKeys,
        fullscreenMediaQuery,
        fullWidth: true,
        getKey: getItemKey,
        getTextValue: (item: T) => optionLabelText(getOptionData(item).label),
        invalid: error,
        isRefreshing,
        items,
        label: fieldLabel,
        labelBehavior: "floating" as const,
        mode: overlayMode,
        placeholder: statusSlot ?? placeholder,
        renderFullscreen,
        renderItem: renderListItem,
        renderValue: (item: T) => getOptionData(item).label,
        required,
        slots: {
            states: {
                empty: ({ surface }: { surface: PickerOverlaySurface }) => (
                    <StatePlaceholder size={overlayContentSize(surface)} title="Нет подходящих опций" />
                ),
                loading: ({ surface }: { surface: PickerOverlaySurface }) => (
                    <StatePlaceholder
                        size={overlayContentSize(surface)}
                        title="Загрузка..."
                        icon={<Spinner size={overlayContentSize(surface)} />}
                    />
                ),
            },
        },
        state: collectionState,
    };

    if (isMultiple) {
        return (
            <div className={className}>
                <SelectField
                    {...commonProps}
                    selectionMode="multiple"
                    selectedKeys={selectedKeys instanceof Set ? selectedKeys : new Set()}
                    onSelectionChange={(keys) => {
                        const next: V[] = [];
                        for (const key of keys) {
                            const id = findValueByKey(key);
                            if (id != null) next.push(id);
                        }
                        // TODO(types): remove assertion — SelectField callbacks не сужают generic M
                        onChange(next as SelectChangeValue<M, V>);
                    }}
                />
            </div>
        );
    }

    return (
        <SelectField
            {...commonProps}
            selectionMode="single"
            selectedKey={typeof selectedKeys === "string" || typeof selectedKeys === "number" ? selectedKeys : null}
            onSelectionChange={(key) => {
                if (key == null) {
                    // TODO(types): remove assertion — SelectField callbacks не сужают generic M
                    onChange(null as SelectChangeValue<M, V>);
                    return;
                }
                const id = findValueByKey(key);
                // TODO(types): remove assertion — SelectField callbacks не сужают generic M
                onChange(id as SelectChangeValue<M, V>);
            }}
        />
    );
}
